import { Amount, isZeroAmount, subtractUntaxedAmounts } from '@mews-distributor/common';
import { CancellationFee } from '@mews-distributor/data-access';
import { PricingMode } from '@mews/enums';
import { LocalizedText } from '@mews/framework';
import { TextKey } from '@mews/text-keys';
import { useAppSelector } from '../../../utils/redux';
import { getLanguageCode } from '../../app/selectors';
import { hasEnterpriseNetPricing } from '../../entities/enterprises/selectors';
import { getServiceEnterpriseId } from '../../entities/services/selectors';
import { localizeCurrencyValue, localizeShortDateAndTimeString } from '../../globalization/selectors';

interface BannerTextProps {
    cancellationFee: CancellationFee;
    serviceId: string;
    timezone: string;
    totalAmount: Amount;
}

export const BannerText = ({ cancellationFee, serviceId, timezone, totalAmount }: BannerTextProps) => {
    const enterpriseId = useAppSelector((state) => getServiceEnterpriseId(state, { serviceId }));

    const amount = useAppSelector((state) => {
        const languageCode = getLanguageCode(state);

        if (enterpriseId == null || languageCode == null) {
            return null;
        }

        const difference = subtractUntaxedAmounts(totalAmount, cancellationFee.amount);

        const value = hasEnterpriseNetPricing(state, { enterpriseId }) ? difference.netValue : difference.grossValue;
        return localizeCurrencyValue(state, {
            currencyValue: { value, currencyCode: difference.currency },
            pricingMode: PricingMode.Simple,
            languageCode,
        });
    });

    const dateStrings = useAppSelector((state) => {
        if (enterpriseId == null || cancellationFee == null) {
            return null;
        }

        return localizeShortDateAndTimeString(state, { timezone, dateTimeUtc: cancellationFee.endUtc });
    });

    if (cancellationFee == null || dateStrings == null || amount == null) {
        return null;
    }

    if (isZeroAmount(cancellationFee.amount)) {
        return (
            <LocalizedText
                textKey={TextKey.FreeCancellationUntilExtended}
                substitutes={{
                    timeShortFormat: dateStrings.shortDateTimeString,
                    dateLongFormat: dateStrings.dateString,
                }}
            />
        );
    }

    if (totalAmount.grossValue > cancellationFee.amount.grossValue) {
        return (
            <LocalizedText
                textKey={TextKey.PartiallyRefundableUntilExtended}
                substitutes={{
                    amount,
                    timeShortFormat: dateStrings.shortDateTimeString,
                    dateLongFormat: dateStrings.dateString,
                }}
            />
        );
    }

    return null;
};
